import { setRTLTextPlugin, addProtocol } from 'maplibre-gl';

import 'maplibre-gl/dist/maplibre-gl.css';
import { Protocol } from 'pmtiles';
import * as R from 'ramda';
import React from 'react';
import Zousan from 'zousan';

import type { BusPoi, BusPoiMap } from '@typings/bus.ts';
import { isCanvasRecordingEnabled, isSurroundEnabled } from '@utils/configUtils.ts';
import { prefersHighContrast } from '@utils/prefersHighContrast.ts';

import type { PluginConfig } from '../config-schema.ts';
import AnimationController from './animationController.ts';
import BadgeController from './badgeController.ts';
import DebugToolsController from './debugToolsController.tsx';
import DynamicPoiStyleController from './dynamicPoiStyleController.ts';
import FloorController from './floorController.ts';
import HeatmapController from './heatmapController.ts';
import LabelController from './labelController.ts';
import MapComponent from './mapComponent.tsx';
import MapThemeController from './mapThemeController.ts';
import MarkerController from './markerController.tsx';
import NavlineController from './navlineController.ts';
import RoutePreviewController from './routePreview/routePreviewController.tsx';
import SelectionController from './selectionController.ts';
import StateController from './stateController.ts';
import { addSurroundTiles } from './surround/addSurroundTiles.ts';
import { hideConflictingLayers } from './surround/conflictingLayers.ts';
import type {
  MapController,
  MapGeoJsonSource,
  MapInitialized,
  MapInstance,
  MapLayerLike,
  MapRendererApp,
  MapStyleLike,
  StyleDefinition,
  SurroundConfig,
  TileServerAuthInfo,
  VenueMapData,
  ViewLimits,
} from './types.ts';
import UserInteractionController from './userInteractionController.ts';
import { addNavLayers, getCanvasCssSize, projectLatLng } from './utils.ts';

// setRTLTextPlugin returns a Promise since maplibre-gl v5. It loads lazily
// (second arg), so guard the rejection to avoid an unhandled promise rejection.
void setRTLTextPlugin(
  'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
  true,
).catch(() => {
  /* RTL plugin failed to load; non-fatal */
});

const protocol = new Protocol();
addProtocol('pmtiles', protocol.tile);

declare global {
  interface Window {
    _mapbox?: MapInstance;
  }
}

type FilterMatch = {
  p: unknown[];
  i: number;
};

type MapStyleWithLayers = MapStyleLike & {
  layers: MapLayerLike[];
  sources: Record<string, MapGeoJsonSource>;
};

// Set by the `postproc-mol-url-parms` post-processor when restoring deep-linked state from
// the URL; not part of the stored/validated plugin config.
type DeepLinkProps = {
  visualTest?: string;
};

const asController = (value: unknown): MapController => (value ?? {}) as MapController;

// Controllers are default-exported factory functions invoked with `new`; cast them
// through this constructor signature so TypeScript accepts the `new` call.
type ControllerConstructor = new (...args: unknown[]) => MapController;

// Style docs may arrive already parsed (the normal venue/mapDataLoaded and
// map/replaceStyle paths) or as a raw JSON string (the debug file-drop path,
// which reads the dropped file via FileReader.readAsText). Normalize both here.
const parseStyle = <T,>(style: T | string): T => (typeof style === 'string' ? (JSON.parse(style) as T) : style);

function create(app: MapRendererApp, config: PluginConfig & { deepLinkProps?: DeepLinkProps }) {
  let mapInitializedPromise = new Zousan<MapInstance>();
  const mapInstanceReceived = new Zousan<MapInstance>();
  let thisMap: MapInstance | null = null;

  // Full Route Preview flag, owned by the online/getDirectionsFromTo plugin.
  // Read cross-plugin to gate the preview segment styling. Interim; defaults off.
  const fullRoutePreviewEnabled = app.config.plugins?.['online/getDirectionsFromTo']?.fullRoutePreviewEnabled === true;

  // Handlers for the currently active venue's map, owned by addSurroundTiles.
  // It replaces these on every venue load; the single bus listeners registered
  // below delegate to them, so switching venues never accumulates duplicate
  // listeners. onSurroundToggle -> high-contrast toggle; onLangChanged ->
  // locality-label refresh on UI language change.
  let onSurroundToggle: ((payload?: { highContrast?: boolean }) => void) | null = null;
  let onLangChanged: (() => void | Promise<void>) | null = null;
  const setSurroundHandlers = ({
    onToggle,
    onLangChanged: onLang,
  }: {
    onToggle: (payload?: { highContrast?: boolean }) => void;
    onLangChanged: () => void | Promise<void>;
  }) => {
    onSurroundToggle = onToggle;
    onLangChanged = onLang;
  };

  const surroundConfig = config.surroundConfig;
  const surroundEnabled = isSurroundEnabled(surroundConfig);

  // The current high-contrast mode, read once from the OS setting on load. The
  // map and the user menu both read it from here instead of checking the OS
  // themselves, so they can't disagree.
  let highContrastEnabled = prefersHighContrast();

  // Log when the OS setting turns high contrast on by default, so we can see how
  // often that happens. The user menu logs the manual toggle separately.
  if (highContrastEnabled) {
    app.bus.send('appInsights/log', {
      name: 'mapRenderer/highContrastOsPreference',
      properties: { pageLocation: 'map load', enabled: true },
    });
  }

  // Handles the user menu toggle: restyles the map and updates the mode here.
  // Does nothing if the mode isn't actually changing.
  const setHighContrast = (enabled: boolean) => {
    if (enabled === highContrastEnabled) {
      return;
    }
    highContrastEnabled = enabled;
    onSurroundToggle?.({ highContrast: enabled });
  };

  const dlp = config.deepLinkProps || {};
  const mapInitialized: MapInitialized = () => mapInitializedPromise;

  const getTerminalPois = async (): Promise<BusPoi[]> => {
    const pois = (await app.bus.get('poi/getAll')) as BusPoiMap | undefined;
    return Object.values(pois ?? {}).filter(poi => poi.category === 'terminal');
  };

  const controllers: MapController[] = [
    asController(new (SelectionController as unknown as ControllerConstructor)(app, mapInitialized)),
    asController(new (UserInteractionController as unknown as ControllerConstructor)(app, mapInitialized, config)),
    asController(new (FloorController as unknown as ControllerConstructor)(app, mapInitialized)),
    new (NavlineController as unknown as ControllerConstructor)(app, mapInitialized, fullRoutePreviewEnabled),
    new (RoutePreviewController as unknown as ControllerConstructor)(app, mapInitialized, fullRoutePreviewEnabled),
    asController(new (MarkerController as unknown as ControllerConstructor)(app, mapInitialized)),
    asController(new (AnimationController as unknown as ControllerConstructor)(app, mapInitialized, config)),
    new (LabelController as unknown as ControllerConstructor)(app, mapInitialized, getTerminalPois, config),
    asController(new (BadgeController as unknown as ControllerConstructor)(app, mapInitialized)),
    asController(new (StateController as unknown as ControllerConstructor)(app, mapInitialized, config)),
    asController(new (MapThemeController as unknown as ControllerConstructor)(app, mapInitialized)),
    asController(new (DynamicPoiStyleController as unknown as ControllerConstructor)(app, mapInitialized)),
    asController(new (HeatmapController as unknown as ControllerConstructor)(app, mapInitialized)),
  ];

  if (app.config.debug) {
    controllers.push(asController(new (DebugToolsController as unknown as ControllerConstructor)(app, mapInitialized)));
  }

  const init = () => {
    app.bus.send('venueData/loadMap');
    if (dlp.visualTest === 'true') {
      app.bus.send('map/visualTest', { hideMap: true });
    }
  };

  const onMapComponentInitialized = (map: MapInstance) => {
    if (thisMap) {
      const oldContainer = thisMap.getContainer();
      const newContainer = map.getContainer();
      newContainer.classList.add('maplibregl-map');
      while (oldContainer.childNodes.length > 0) {
        newContainer.appendChild(oldContainer.childNodes[0]);
      }
    }
    thisMap = map;
    controllers.forEach(controller => controller.onMapInitialized?.(map));
    mapInstanceReceived.resolve(thisMap);
  };

  const viewLimits = R.pick(['minZoom', 'maxZoom', 'minPitch', 'maxPitch'], config.viewLimits ?? {}) as ViewLimits;

  app.bus.send('layers/register', {
    id: 'mapRenderer',
    widget: () => (
      <MapComponent
        onInit={onMapComponentInitialized}
        viewLimits={viewLimits}
        surroundConfig={surroundEnabled ? (surroundConfig as SurroundConfig) : undefined}
        preserveDrawingBuffer={isCanvasRecordingEnabled(app.config.plugins?.posthog)}
      />
    ),
    layoutId: 'map',
    layoutName: app.config.initialLayout || 'standard',
  });

  const buildLayers = (style: MapStyleWithLayers): MapLayerLike[] => {
    const startingLayers = style.layers;
    const newLayers = controllers.reduce(
      (layers, controller) => (controller.initMapLayers ? controller.initMapLayers(layers) : layers),
      startingLayers,
    );
    const fixLayer = (layer: MapLayerLike): MapLayerLike[] => {
      if (layer.source && !style.sources[layer.source]) {
        console.warn(`No '${layer.source}' source found for layer '${layer.id}'`);
        return [];
      }
      const { ['source-layer']: sourceLayer, ...rest } = layer;
      void sourceLayer;
      return [rest];
    };
    return newLayers.flatMap(fixLayer);
  };

  const buildInitialMapStyle = ({
    mapStyleSource,
    badgesSpriteUrl,
    mapGlyphsUrl,
  }: VenueMapData): MapStyleWithLayers => {
    const rawMapStyle = parseStyle<MapStyleLike>(mapStyleSource as MapStyleLike | string);
    const style = {
      ...R.mergeRight(rawMapStyle, {
        sprite: badgesSpriteUrl,
        glyphs: mapGlyphsUrl,
      }),
    } as MapStyleWithLayers & Record<string, unknown>;

    if (!Array.isArray(style.layers)) {
      style.layers = [];
    }
    style.sources = {};

    delete style.bearing;
    delete style.name;
    delete style.owner;
    delete style.pitch;
    delete style.visibility;
    delete style.zoom;

    style.sources = controllers.reduce<Record<string, MapGeoJsonSource>>(
      (acc, controller) => (controller.initMapSources ? controller.initMapSources(acc) : acc),
      {},
    );
    style.layers = buildLayers(style);
    augmentStyle(style);
    return style;
  };

  function addTilemapLayer(map: MapInstance, tileServerAuthInfo: TileServerAuthInfo) {
    map.on('load', () => {
      map.addSource('tiles', {
        type: 'raster',
        tiles: [tileServerAuthInfo.tileUrl],
        minzoom: 14,
        maxzoom: 24,
      });

      map.addLayer(
        {
          id: 'tiles',
          type: 'raster',
          source: 'tiles',
        },
        'outdoor',
      );
    });
  }

  function addSurroundLayers(
    map: MapInstance,
    { venueCenter, surroundConfig }: Pick<VenueMapData, 'venueCenter'> & { surroundConfig: SurroundConfig },
  ) {
    const loadedAddSurround = addSurroundTiles(
      app,
      map,
      { venueCenter, surroundConfig },
      protocol,
      setSurroundHandlers,
      () => highContrastEnabled,
    );
    if (map.loaded()) {
      loadedAddSurround();
    } else {
      map.on('load', loadedAddSurround);
    }
  }

  const findFilterItems = (style: MapStyleWithLayers, name: string) =>
    findStringsInNestedArrays(
      style.layers.map(layer => (Array.isArray(layer.filter) ? layer.filter : [])),
      name,
    );

  const findStringsInNestedArrays = (items: unknown[], name: string): FilterMatch[] =>
    items.reduce((matches: FilterMatch[], item, i) => {
      if (typeof item === 'string') {
        return item === name ? matches.concat({ p: items, i }) : matches;
      }
      if (Array.isArray(item)) {
        return matches.concat(findStringsInNestedArrays(item, name));
      }
      return matches;
    }, []);

  const augmentStyleForMultipoint = (style: MapStyleWithLayers) => {
    findFilterItems(style, 'nav.alternative').forEach(match => {
      const alreadyAugmented = match.p.includes('nav.multipoint') || match.p.includes('nav.alternativemultipoint');
      if (!alreadyAugmented) {
        match.p.splice(match.i, 0, 'nav.multipoint', 'nav.alternativemultipoint');
      }
    });

    const linePaint = style.layers.find(layer => layer.id === 'nav - alternative')?.paint;
    if (linePaint) {
      linePaint['line-opacity'] = ['get', 'fillColorOpacity'];
    }
  };

  const augmentStyle = (style: MapStyleWithLayers) => {
    addNavLayers(style, fullRoutePreviewEnabled);
    augmentStyleForMultipoint(style);
    if (surroundEnabled) {
      hideConflictingLayers(style as unknown as StyleDefinition);
    }
  };

  const initializeMap = ({
    venueCenter,
    style,
    map,
    tileServerAuthInfo,
  }: VenueMapData & { style: MapStyleWithLayers; map: MapInstance }) => {
    if (app.config.debug && typeof window !== 'undefined') {
      window._mapbox = map;
    }
    map.setStyle(style as Parameters<typeof map.setStyle>[0], { diff: false });
    map.once('styledata', () => {
      mapInitializedPromise.resolve(map);
    });
    if (config.padding) {
      app.bus.send('map/changePadding', { padding: config.padding });
    }
    if (tileServerAuthInfo && 'tileUrl' in tileServerAuthInfo) {
      addTilemapLayer(map, tileServerAuthInfo as TileServerAuthInfo);
    }
    if (surroundEnabled) {
      addSurroundLayers(map, {
        venueCenter,
        surroundConfig: surroundConfig as SurroundConfig,
      });
    }
  };

  app.bus.on<VenueMapData>('venueData/mapDataLoaded', async params => {
    const initialMapStyle = buildInitialMapStyle(params);
    mapInstanceReceived.then(map => initializeMap({ ...params, style: initialMapStyle, map }));
    await app.bus.send('layers/show', { id: 'mapRenderer' });
  });

  // Registered once for the plugin's lifetime. Both read/write the mode above so
  // the map and user menu stay in sync.
  app.bus.on<{ highContrast?: boolean }>('surround/setHighContrast', ({ highContrast } = {}) =>
    setHighContrast(!!highContrast),
  );
  app.bus.on('surround/getHighContrast', () => highContrastEnabled);
  app.bus.on('i18n/langChanged', () => onLangChanged?.());

  app.bus.on('venueData/loadNewVenue', () => {
    mapInitializedPromise = new Zousan<MapInstance>();
    init();
  });

  app.bus.on<{ px?: number }>('map/blur', ({ px }) =>
    mapInitialized().then(map => {
      map.getCanvas().style.filter = !px ? '' : `blur(${px}px)`;
    }),
  );

  app.bus.on('map/getSnapshot', () =>
    mapInitialized().then(
      map =>
        new Promise<string>((resolve, reject) => {
          map.once('render', () => {
            const canvas = map.getCanvas();
            if (!canvas) {
              reject(Error('Map canvas unavailable for snapshot capture'));
            } else {
              resolve(canvas.toDataURL('image/png'));
            }
          });
          map.triggerRepaint();
        }),
    ),
  );

  app.bus.on<{ lat: number; lng: number }>('map/project', ({ lat, lng }) =>
    mapInitialized().then(map => projectLatLng(map, { lat, lng })),
  );

  app.bus.on('map/getCanvasCssSize', () => mapInitialized().then(map => getCanvasCssSize(map)));

  app.bus.on('map/resize', () => mapInitialized().then(map => map.resize()));

  app.bus.on<{ styleSrc: MapStyleWithLayers | string }>('map/replaceStyle', ({ styleSrc }) => {
    mapInitialized().then(map => {
      const loadedStyle = parseStyle<MapStyleWithLayers>(styleSrc);
      const currentStyle = map.getStyle() as MapStyleWithLayers;
      loadedStyle.sources = { ...currentStyle.sources };
      const newLayers = buildLayers(loadedStyle);
      const newStyle = { ...currentStyle, layers: newLayers };
      augmentStyle(newStyle);
      map.setStyle(newStyle as Parameters<typeof map.setStyle>[0]);
    });
  });

  app.bus.on('map/resize', () => mapInitialized().then(map => map.resize()));

  return {
    init,
  };
}

export { create };
